import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box } from '@mui/material';
import {
  groupColor,
  INK,
  MUTED,
  npsPill,
  SECONDARY,
  SURFACE_ALT,
  tableHeadCellSx,
} from 'client/modules/Analyze/analyzeTheme';
import buildBreakdownDrillUrl from 'client/modules/Analyze/buildBreakdownDrillUrl';
import getCountryFlag from 'client/modules/Analyze/countryFlag';
import groupsByType from 'client/modules/Surveys/pages/DashboardPage/groupsByType';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

const messages = defineMessages({
  responses: { id: 'analyze.breakdown.column.responses', defaultMessage: 'Responses' },
  comments: { id: 'analyze.breakdown.column.comments', defaultMessage: 'Comments' },
  score: { id: 'analyze.breakdown.column.score', defaultMessage: 'Score' },
  nps: { id: 'analyze.breakdown.column.nps', defaultMessage: 'NPS' },
  split: { id: 'analyze.breakdown.column.split', defaultMessage: 'Composition' },
  promoters: { id: 'Group.Promoters', defaultMessage: 'Promoter' },
  passives: { id: 'Group.Passives', defaultMessage: 'Passive' },
  detractors: { id: 'Group.Detractors', defaultMessage: 'Detractor' },
  agree: { id: 'Group.Agree', defaultMessage: 'Agree' },
  neutral: { id: 'Group.Neutral', defaultMessage: 'Neutral' },
  disagree: { id: 'Group.Disagree', defaultMessage: 'Disagree' },
  satisfied: { id: 'Group.Satisfied', defaultMessage: 'Satisfied' },
  dissatisfied: { id: 'Group.Dissatisfied', defaultMessage: 'Dissatisfied' },
  stars: {
    id: 'Dashboard.Tooltip.Stars',
    defaultMessage: '{num, number}-{num, plural, one {star} other {stars}}',
  },
  openDrill: { id: 'analyze.breakdown.openDrill', defaultMessage: 'View in responses' },
});

const groupMessageKeys = {
  promoters: 'promoters',
  passives: 'passives',
  detractors: 'detractors',
  agree: 'agree',
  neutral: 'neutral',
  disagree: 'disagree',
  satisfied: 'satisfied',
  dissatisfied: 'dissatisfied',
};

function getSortValue(row, column) {
  if (column === 'responses') {
    return row.responses ?? 0;
  }
  if (column === 'comments') {
    return row.comments?.count ?? 0;
  }
  if (column === 'nps') {
    return row.nps ?? -Infinity;
  }
  return 0;
}

function SortHeader({ column, label, sortBy, sortDir, onSort }) {
  const active = sortBy === column;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onSort(column)}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onSort(column)}
      sx={{
        ...tableHeadCellSx,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        color: active ? SECONDARY : tableHeadCellSx.color,
        '&:hover': { color: SECONDARY },
      }}
    >
      {label}
      <Box
        component="span"
        sx={{ fontSize: 9, lineHeight: 1, visibility: active ? 'visible' : 'hidden' }}
      >
        {sortDir === 'asc' ? '▲' : '▼'}
      </Box>
    </Box>
  );
}

SortHeader.propTypes = {
  column: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortDir: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
};

function BreakdownTable({
  rows,
  surveyId,
  surveyType,
  dimension,
  propertyKey,
  dimensionControl,
  placeholder,
}) {
  const { formatMessage, formatNumber, locale } = useIntl();
  const groups = groupsByType[surveyType] || groupsByType.nps;
  const isNps = surveyType !== 'star_five';
  const [sortBy, setSortBy] = useState('responses');
  const [sortDir, setSortDir] = useState('desc');

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const aVal = getSortValue(a, sortBy);
      const bVal = getSortValue(b, sortBy);
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, sortBy, sortDir]);

  const handleSort = column => {
    if (sortBy === column) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  const formatGroupLabel = group => {
    if (surveyType === 'star_five') {
      const [num] = group.split('-');
      return formatMessage(messages.stars, { num: Number(num) });
    }
    const key = groupMessageKeys[group];
    return key ? formatMessage(messages[key]) : group;
  };

  const scoreLabel = isNps ? formatMessage(messages.nps) : formatMessage(messages.score);
  const splitHeaderLabel =
    groups.length <= 3 ? groups.map(formatGroupLabel).join(' / ') : formatMessage(messages.split);

  const gridCols = isNps ? '1.3fr .9fr 1.1fr .8fr 2.4fr' : '1.3fr .9fr 1.1fr 2.4fr';

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #ECEFF3',
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 4px 12px rgba(16,24,40,.04)',
        overflow: 'hidden',
      }}
    >
      {/* header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          alignItems: 'center',
          px: 3.25,
          py: 0.75,
          bgcolor: SURFACE_ALT,
          borderBottom: '1px solid #ECEFF3',
        }}
      >
        <Box>{dimensionControl}</Box>
        <SortHeader
          column="responses"
          label={formatMessage(messages.responses)}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
        />
        <SortHeader
          column="comments"
          label={formatMessage(messages.comments)}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
        />
        {isNps && (
          <SortHeader
            column="nps"
            label={scoreLabel}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
          />
        )}
        <Box sx={{ ...tableHeadCellSx, textAlign: 'right' }}>{splitHeaderLabel}</Box>
      </Box>

      {/* body */}
      {placeholder ? (
        <Box sx={{ p: 3 }}>{placeholder}</Box>
      ) : (
        sortedRows.map(row => {
          const pill = npsPill(row.nps);
          const segments = groups
            .map(group => ({
              group,
              color: groupColor(group),
              pct: row[group]?.percent ?? 0,
            }))
            .filter(s => s.pct > 0);
          const splitLabel = groups
            .map(group => formatNumber(Math.round(row[group]?.percent ?? 0)))
            .join(' / ');
          const flag = getCountryFlag(row.label, locale);
          return (
            <Box
              key={row.savedSearchId || row.label}
              component={Link}
              to={buildBreakdownDrillUrl({
                surveyId,
                dimension,
                propertyKey,
                rowValue: row.label,
                savedSearchId: row.savedSearchId,
              })}
              title={formatMessage(messages.openDrill)}
              sx={{
                display: 'grid',
                gridTemplateColumns: gridCols,
                alignItems: 'center',
                px: 3.25,
                py: 1,
                borderBottom: '1px solid #F1F4F8',
                textDecoration: 'none',
                color: INK,
                cursor: 'pointer',
                '&:hover': { bgcolor: '#FAFBFD' },
                '&:hover .drill-ic': { opacity: 1 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  minWidth: 0,
                  fontSize: 14,
                  fontWeight: 600,
                  color: INK,
                }}
              >
                {flag && (
                  <Box component="span" sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
                    {flag}
                  </Box>
                )}
                <Box
                  component="span"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {row.label}
                </Box>
                <OpenInNewIcon
                  className="drill-ic"
                  sx={{
                    fontSize: 15,
                    color: MUTED,
                    opacity: 0,
                    transition: 'opacity .15s',
                    flexShrink: 0,
                  }}
                />
              </Box>
              <Box
                sx={{
                  textAlign: 'right',
                  fontSize: 14,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatNumber(row.responses)}
              </Box>
              <Box
                sx={{
                  textAlign: 'right',
                  fontSize: 14,
                  color: SECONDARY,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatNumber(row.comments?.count ?? 0)}
                {row.comments?.percent != null && ` (${formatNumber(row.comments.percent)}%)`}
              </Box>
              {isNps && (
                <Box sx={{ textAlign: 'right' }}>
                  {row.nps != null ? (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        minWidth: 42,
                        textAlign: 'center',
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: pill.color,
                        bgcolor: pill.bg,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: '7px',
                      }}
                    >
                      {formatNumber(row.nps)}
                    </Box>
                  ) : (
                    '—'
                  )}
                </Box>
              )}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-end' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    height: 14,
                    width: 230,
                    maxWidth: '100%',
                    borderRadius: '7px',
                    overflow: 'hidden',
                    bgcolor: '#EEF1F5',
                    flexShrink: 1,
                  }}
                  title={groups
                    .map(g => `${formatGroupLabel(g)}: ${formatNumber(row[g]?.percent ?? 0)}%`)
                    .join('  ·  ')}
                >
                  {segments.map(s => (
                    <Box key={s.group} sx={{ width: `${s.pct}%`, bgcolor: s.color }} />
                  ))}
                </Box>
                <Box
                  sx={{
                    fontSize: 12.5,
                    color: MUTED,
                    fontVariantNumeric: 'tabular-nums',
                    width: 120,
                    flexShrink: 0,
                  }}
                >
                  {splitLabel}
                </Box>
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
}

BreakdownTable.propTypes = {
  rows: PropTypes.array.isRequired,
  surveyId: PropTypes.string.isRequired,
  surveyType: PropTypes.string.isRequired,
  dimension: PropTypes.string.isRequired,
  propertyKey: PropTypes.string,
  dimensionControl: PropTypes.node,
  placeholder: PropTypes.node,
};

export default BreakdownTable;
